import { useMediaQuery, useTheme } from "@mui/material";

// ============================================================================
// レスポンシブ密度システム（iPad / PC の二密度）
// ----------------------------------------------------------------------------
// desktop と判定する条件 = 画面幅 lg(1200px) 以上 かつ マウス主体(pointer:fine + hover:hover)。
// iPad は幅に依らず tablet 扱い(指タッチ=pointer:coarse/hover:none)。
// 生の px を直書きせず、この ui トークンを density で切り替える(唯一の真実)。
// ============================================================================

/** desktop 判定の最小幅。iPad は幅に依らず tablet。 */
export const DESKTOP_BREAKPOINT = "lg" as const; // >= 1200px

export type DeviceClass = "tablet" | "desktop";

export function useDeviceClass(): DeviceClass {
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT), { noSsr: true });
  const isMousePrimary = useMediaQuery("(pointer: fine) and (hover: hover)", { noSsr: true });
  if (isWide && isMousePrimary) {
    return "desktop";
  }
  return "tablet";
}

export function useIsDesktop(): boolean {
  return useDeviceClass() === "desktop";
}

type Densities<T> = Record<DeviceClass, T>;

export const ui = {
  header: {
    height: { tablet: 96, desktop: 56 } satisfies Densities<number>,
    titleFont: { tablet: 30, desktop: 22 } satisfies Densities<number>,
    backIcon: { tablet: 36, desktop: 28 } satisfies Densities<number>,
    leftMinWidth: { tablet: 200, desktop: 170 } satisfies Densities<number>,
    rightMinWidth: { tablet: 450, desktop: 360 } satisfies Densities<number>,
  },
  clock: {
    font: { tablet: 18, desktop: 14 } satisfies Densities<number>,
    width: { tablet: 205, desktop: 168 } satisfies Densities<number>,
  },
  homeIcon: { tablet: 64, desktop: 44 } satisfies Densities<number>,
  reloadIcon: { tablet: 24, desktop: 20 } satisfies Densities<number>,
  reloadFont: { tablet: 18, desktop: 14 } satisfies Densities<number>,
  menu: {
    maxWidth: { tablet: 800, desktop: 760 } satisfies Densities<number>,
    outerPad: { tablet: 32, desktop: 20 } satisfies Densities<number>,
    titleBlockGap: { tablet: 21.6, desktop: 14 } satisfies Densities<number>,
    headBlockGap: { tablet: 27.2, desktop: 14 } satisfies Densities<number>,
    sectionsGap: { tablet: 36.7, desktop: 22 } satisfies Densities<number>,
    sectionInnerGap: { tablet: 16, desktop: 10 } satisfies Densities<number>,
    rowGap: { tablet: 25, desktop: 14 } satisfies Densities<number>,
    colGap: { tablet: 26.4, desktop: 16 } satisfies Densities<number>,
    logo: { tablet: 100, desktop: 60 } satisfies Densities<number>,
    appTitleFont: { tablet: 27, desktop: 20 } satisfies Densities<number>,
    sectionTitleFont: { tablet: 27, desktop: 18 } satisfies Densities<number>,
    buttonHeight: { tablet: 120, desktop: 80 } satisfies Densities<number>,
    buttonIcon: { tablet: 42.5, desktop: 32 } satisfies Densities<number>,
    buttonLabelFont: { tablet: 20, desktop: 15 } satisfies Densities<number>,
    buttonInnerGap: { tablet: 16.5, desktop: 9 } satisfies Densities<number>,
  },
  grid: {
    rowHeight: { tablet: 62, desktop: 44 } satisfies Densities<number>,
    headerHeight: { tablet: 62, desktop: 44 } satisfies Densities<number>,
    headerFont: { tablet: 18, desktop: 15 } satisfies Densities<number>,
    cellFont: { tablet: 18, desktop: 14 } satisfies Densities<number>,
  },
  page: {
    padX: { tablet: 4, desktop: 3 } satisfies Densities<number>,
    padY: { tablet: 4, desktop: 3 } satisfies Densities<number>,
    maxWidth: { tablet: 1200, desktop: 1280 } satisfies Densities<number>,
  },
};
